import { cn } from '@repo/lib/cn';
import { hms, hmsToSec } from '@repo/lib/time/timeUtils';
import { quizBuilderOptionsSchema, quizBuilderSectionsSchema } from '@repo/schemas/quiz/quizBuilder';
import { AlertCircleIcon, Loader2Icon, PlusIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { GradeScaleField } from '@/components/features/grade-scale';
import QuizBuilderDateTimeSelector from '@/components/features/quiz/quiz-builder/QuizBuilderDateTimeSelector';
import QuizBuilderForceEditAlertDialog from '@/components/features/quiz/quiz-builder/QuizBuilderForceEditAlertDialog';
import QuizBuilderMembersDialog from '@/components/features/quiz/quiz-builder/QuizBuilderMembersDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state/EmptyState';
import { FeaturedIcon } from '@/components/ui/featured-icon/FeaturedIcon';
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { TimeInput, TimeInputItem } from '@/components/ui/input';
import { PointIcon } from '@/components/ui/quiz/point-icon';
import { RadioGroup, RadioGroupItemWithExpansion, RadioGroupItemWithLabel } from '@/components/ui/radio-group';
import { NumberSelect } from '@/components/ui/select';
import { Tag } from '@/components/ui/tags';
import { QUIZ_OPTIONS_FORM_ID } from '@/context/quiz-builder';
import { useQuizBuilderSettings, useQuizBuilderSubmit } from '@/hooks/quiz';
import { formErrorToast, formatZodErrors } from '@/utils/form/formErrorToast';
import { getAvatar } from '@/utils/getAvatar';

export default function QuizBuilderSettingsPage() {
  const { optionsForm, sectionsForm, users, groups, setUsers, setGroups, removeUser, removeGroup, membersDialogOpen, setMembersDialogOpen, pointsCount, questionsCount, sectionsCount } =
    useQuizBuilderSettings();

  const { onSubmit, isPending, isError, errorMessage, openAlert, setOpenAlert } = useQuizBuilderSubmit(users, groups);

  // Compute section form errors directly from form state
  const sectionFormErrors = useMemo<string[]>(() => {
    const values = sectionsForm.getValues();
    const zodResult = quizBuilderSectionsSchema.safeParse(values);
    if (!zodResult.success) {
      return formatZodErrors(zodResult.error);
    }
    return [];
  }, [sectionsForm]);

  const gradeMapping = useWatch({
    control: optionsForm.control,
    name: 'gradeMapping',
  });

  const handleOnConfirm = () => {
    optionsForm.handleSubmit(
      (data) => {
        onSubmit(data, true);
      },
      () => {
        formErrorToast(optionsForm, quizBuilderOptionsSchema);
      }
    )();
  };

  const noUsersOrGroups: boolean = users.length === 0 && groups.length === 0;

  return (
    <>
      <QuizBuilderMembersDialog open={membersDialogOpen} onOpenChange={setMembersDialogOpen} users={users} groups={groups} onUsersChange={setUsers} onGroupsChange={setGroups} />
      <QuizBuilderForceEditAlertDialog open={openAlert} onOpenChange={setOpenAlert} onConfirm={handleOnConfirm} />

      <Form {...optionsForm}>
        <form
          noValidate
          id={QUIZ_OPTIONS_FORM_ID}
          onSubmit={optionsForm.handleSubmit(
            (data) => onSubmit(data, false),
            () => {
              formErrorToast(optionsForm, quizBuilderOptionsSchema);
            }
          )}
          className="flex grow flex-col overflow-auto"
        >
          {/* inline error box */}
          {(isError || sectionFormErrors.length > 0) && (
            <div className="space-y-3 px-3 pt-3">
              {isError && (
                <Alert variant={'destructive'} className="bg-destructive/5 border-border-destructive">
                  <AlertCircleIcon />
                  <AlertTitle>{errorMessage.title}</AlertTitle>
                  <AlertDescription>{errorMessage.detail}</AlertDescription>
                </Alert>
              )}
              {sectionFormErrors.length > 0 && (
                <Alert variant={'destructive'} className="bg-destructive/5 border-border-destructive">
                  <AlertCircleIcon />
                  <AlertTitle>Błędy w sekcjach quizu</AlertTitle>
                  <AlertDescription>
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {sectionFormErrors.map((msg, idx) => (
                        <span key={idx}>
                          {idx > 0 && '\n'}
                          {msg}
                        </span>
                      ))}
                    </span>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          {/* dim + spinner overlay */}
          {isPending && (
            <div className={'bg-background/50 absolute inset-0 z-20 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-[2px]'}>
              <Loader2Icon className={'size-4 animate-spin'} aria-hidden={'true'} />
              <h5 className={'font-medium'}>Zapisywanie...</h5>
            </div>
          )}
          <div className="border-border-primary flex flex-col gap-4 border-b p-4 md:flex-row">
            <div className="flex h-full flex-col justify-center space-y-1">
              <div className="text-2xl font-semibold text-nowrap">Opcje Quizu</div>
              <div className="text-muted-foreground text-sm text-nowrap">Wypełnij opcje quizu</div>
            </div>
            <div className="flex w-full flex-row items-center gap-6 md:justify-end">
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm font-medium">Suma punktów</p>
                <div className="flex h-4.5 items-center gap-1">
                  <PointIcon />
                  <span className="text-accent-foreground text-sm font-medium">{pointsCount}</span>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-text-tertiary text-sm font-medium">Ilość sekcji</p>
                <div className="flex h-4.5 items-center gap-1">
                  <span className="text-accent-foreground text-sm font-medium">{sectionsCount}</span>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-text-tertiary text-sm font-medium">Ilość pytań</p>
                <div className="flex h-4.5 items-center gap-1">
                  <span className="text-accent-foreground text-sm font-medium">{questionsCount}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grow grid-cols-1 gap-0 md:grid-cols-2">
            <div className="md:border-border-primary w-full space-y-8 p-4 md:border-r md:border-b-0">
              <div>
                <h5 className="mb-3 text-lg font-semibold">Ilość podejść</h5>
                <FormField
                  name="maxAttempts"
                  render={({ field }) => {
                    const radioGroupValue = field.value === 0 ? '1' : '2';
                    return (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value === '1' ? 0 : 1);
                            }}
                            value={radioGroupValue}
                          >
                            <RadioGroupItemWithLabel value="1" label="Nieograniczony ilość podejść" description="Użytkownik ma nieograniczoną ilość podejść dla quizu" />
                            <RadioGroupItemWithExpansion
                              value="2"
                              label="Ustaw ilość podejść"
                              description="Ustaw ilość możiwych podejść dla użytkownika"
                              selectedValue={radioGroupValue}
                              expansionContent={
                                <NumberSelect
                                  {...field}
                                  value={String(field.value)}
                                  onValueChange={(value) => {
                                    field.onChange(Number(value));
                                  }}
                                  disabled={radioGroupValue !== '2'}
                                  start={1}
                                  end={10}
                                  size="sm"
                                  placeholder="Ilość podejść"
                                  className="w-30"
                                />
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              <div className="space-y-3">
                <h5 className="mb-3 text-lg font-semibold">Czas wykonania</h5>
                <FormField
                  name="time"
                  render={({ field }) => {
                    const radioGroupValue = field.value === 0 ? '1' : '2';
                    return (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value === '1' ? 0 : 60 * 45);
                            }}
                            value={radioGroupValue}
                            className="gap-6"
                          >
                            <RadioGroupItemWithLabel value="1" label="Nieograniczony czas quizu" description="Użytkownik ma nieograniczony czas na każdą sekcję gdy sekcje nie mają podanego czasu" />
                            <RadioGroupItemWithExpansion
                              value="2"
                              label="Ustaw całkowity czas quizu"
                              description="Ogranicza sekcję które nie mają ustawionego czasu"
                              selectedValue={radioGroupValue}
                              expansionContent={
                                <>
                                  <FormDescription className="mb-1.5">(hh:mm:ss)</FormDescription>
                                  <TimeInput className="max-w-34" disabled={field.disabled} value={hms(field.value)} onChange={(value) => field.onChange(hmsToSec(value))} inputProps={{ ...field }}>
                                    <TimeInputItem picker="hours" onBlur={field.onBlur} />
                                    <TimeInputItem picker="minutes" onBlur={field.onBlur} />
                                    <TimeInputItem picker="seconds" ref={field.ref} onBlur={field.onBlur} />
                                  </TimeInput>
                                </>
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              <div className="space-y-3">
                <h5 className="mb-3 text-lg font-semibold">Tryb quizu</h5>
                <FormField
                  name="freeNavigation"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={(value) => {
                            field.onChange(value === '1' ? false : true);
                          }}
                          value={field.value ? '2' : '1'}
                          className="gap-6"
                        >
                          <RadioGroupItemWithLabel value="1" label="Brak możliwości cofania do sekcji" description="Sekcje są wyświetlane jedno po drugim bez możliwości cofania się" />
                          <RadioGroupItemWithLabel
                            value="2"
                            label="Można się cofać do sekcji"
                            description="Gdy każda sekcja ma osobno ustawiony czas wtedy liczona jest suma czasów dla poszczególnych sekcji."
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="space-y-3">
                <h5 className="mb-3 text-lg font-semibold">Wyświetl wynik</h5>
                <FormField
                  name="instantMark"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={(value) => {
                            field.onChange(value === '1' ? false : true);
                          }}
                          value={field.value ? '2' : '1'}
                          className="gap-6"
                        >
                          <RadioGroupItemWithLabel value="1" label="Wyświetl na końcu quizu" description="Użytkownik otrzyma wynik na końcu quizu" />
                          <RadioGroupItemWithLabel value="2" label="Wyświetl po każdej sekcji" description="Użytkowink otrzyma wynik po każdej sekcji" />
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <GradeScaleField
                gradeMapping={gradeMapping}
                onChange={(next) =>
                  optionsForm.setValue('gradeMapping', next, {
                    shouldDirty: true,
                  })
                }
                headingClassName="text-lg"
              />
            </div>
            <div className="w-full space-y-8 p-4">
              <div className="space-y-3">
                <h5 className="mb-3 text-lg font-semibold">Dostęp czasowy</h5>
                <div className="flex gap-3">
                  <QuizBuilderDateTimeSelector />
                </div>
              </div>
              <div>
                <div className="mb-3 flex w-full flex-row items-center gap-3">
                  <h5 className="text-lg font-semibold">Dostęp użytkowników</h5>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {groups.map(({ id, name, avatarURL }) => (
                    <Tag key={id} onRemove={() => removeGroup(id)} variant={'muted'} border>
                      <Avatar size={'xs'}>
                        <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name} />
                        <AvatarFallback>
                          <UsersIcon />
                        </AvatarFallback>
                      </Avatar>
                      {name}
                    </Tag>
                  ))}
                  {users.map(({ id, name, surname, avatarURL }) => (
                    <Tag key={id} onRemove={() => removeUser(id)} variant={'muted'} border>
                      <Avatar size={'xs'}>
                        <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name + '_' + surname} />
                        <AvatarFallback>
                          <UserIcon />
                        </AvatarFallback>
                      </Avatar>
                      {name} {surname.at(0)}.
                    </Tag>
                  ))}
                  <Badge variant="muted" border onClick={() => setMembersDialogOpen(true)} className={cn('cursor-pointer', noUsersOrGroups && 'hidden')}>
                    <PlusIcon />
                    Dodaj
                  </Badge>

                  {noUsersOrGroups && (
                    <EmptyState className="border-border-secondary bg-background-hover/25 w-full rounded-md border pt-4!" onClick={() => setMembersDialogOpen(true)}>
                      <FeaturedIcon size={'md'}>
                        <UsersIcon />
                      </FeaturedIcon>
                      <EmptyStateContent>
                        <EmptyStateTitle className="text-sm!">Brak wybranych użytkowników bądź grup</EmptyStateTitle>
                        <EmptyStateDescription className="max-w-xs text-xs!">Wybierz użytkowników lub grupy które będą miały dostęp do quizu</EmptyStateDescription>
                        <EmptyStateFooter>
                          <Button type={'button'} variant={'tertiary'} size="sm" className="min-w-32">
                            <PlusIcon />
                            Dodaj
                          </Button>
                        </EmptyStateFooter>
                      </EmptyStateContent>
                    </EmptyState>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
}
